import type {
  ReplaySettings,
  ReplaySettingsChanges,
  ReplaySettingsInput,
} from '@matter_extractor/shared';
import { replaySettingsChangesSchema, replaySettingsSchema } from '@matter_extractor/shared';
import { ApiError } from './api';

/**
 * The calls of Réglages > Rejeu (docs/specs/rejeu.md §10.3, §11.1, issue #193):
 * the three texts of the instruction a replay serves to Claude, and the journal
 * of their saves.
 *
 * ## Why a file of its own rather than another family in `lib/api.ts`
 *
 * The reason `rules-api.ts` and `links-api.ts` give: `api.ts` is a four-hundred
 * line module several lots edit at once, and a merge that goes wrong there goes
 * wrong in the one place it cannot be seen. `ApiError` is IMPORTED from it, not
 * re-declared: two classes of the same name would make `instanceof` answer
 * false on one of them.
 *
 * The `request` helper is the same deliberate duplication, with the same one
 * behaviour to keep: `details` falls back to the WHOLE body.
 *
 * Every answer is PARSED before it is returned: a response is an external input
 * even when we wrote the server.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

/** The credential travels in a cookie and NEVER in the URL. */
const ADMIN_INIT = {
  headers: { 'content-type': 'application/json' },
  credentials: 'include',
} as const satisfies RequestInit;

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, { ...ADMIN_INIT, ...init });
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      details = body.details ?? body;
      code = typeof body.error === 'string' ? body.error : null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response.json();
}

/**
 * The three texts, the instant and the author of the last save, and how many
 * replays were already played - or `null` when the replay is CLOSED.
 *
 * A 404 IS the closed state and not an error (§4): on a server where
 * `REPLAY_ENABLED` is not `true` the routes answer 404 and the tab does not
 * exist. The same call therefore decides both whether the tab is drawn and what
 * the tab shows; every other failure (a 401, a 500, the network) is left to the
 * query, which is where every screen of this administration reads its errors.
 */
export async function fetchReplaySettings(): Promise<ReplaySettings | null> {
  try {
    const body = await request('/api/replay-settings');
    return replaySettingsSchema.parse(body);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** The journal « Ce qui a déjà bougé », newest first, the seed line included. */
export function fetchReplaySettingsChanges(): Promise<ReplaySettingsChanges> {
  return request('/api/replay-settings/changes').then((body) =>
    replaySettingsChangesSchema.parse(body),
  );
}

/**
 * Replaces the three texts AT ONCE and writes one line of the journal. The
 * reason, when there is one, travels in the same body: the line and the texts
 * are one write on the server, and two requests would leave a moved text with
 * no line to say why.
 */
export function saveReplaySettings(input: ReplaySettingsInput): Promise<ReplaySettings> {
  return request('/api/replay-settings', { method: 'PUT', body: JSON.stringify(input) }).then(
    (body) => replaySettingsSchema.parse(body),
  );
}
